import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map, merge, startWith } from 'rxjs';
import { AccountService } from '@core/providers/account/account.service';
import { IntercomService } from '@core/providers/backend/intercom.service';
import { PermissionsBackendService } from '@core/providers/backend/permissions-backend.service';
import { ContextService } from '@core/providers/context/context.service';
import { ThemeService } from '@core/providers/theme/theme.service';
import { CONSTANTS } from '@shared/constants';
import { type ContextObject } from '@shared/interfaces/login-object';
import { type PermissionsEntry } from '@shared/interfaces/permissions.interface';
import { ContextSwitcherComponent } from '../context-switcher/context-switcher.component';
import { NavModelService } from '../navigation/nav-model.service';
import { ShellNavService } from '../shell-nav.service';
import { type GlobalHeaderAction, GlobalHeaderExtrasService } from './global-header-extras.service';
import {
  type BreadcrumbItem,
  buildGlobalHeaderStaticModel,
  isNavigationEnd,
} from './global-header.utils';

/** A context's own root, e.g. `/account/<uuid>`. The section crumb goes directly after it. */
const CONTEXT_ROOT_URL = new RegExp(
  `^/(${CONSTANTS.ROUTES.ACCOUNT}|${CONSTANTS.ROUTES.VENUE})/[^/]+$`
);

type GlobalHeaderViewModel = Readonly<{
  title: string;
  titleCount: string | number | null;
  breadcrumbs: ReadonlyArray<BreadcrumbItem>;
  helpArticleId: string | null;
  printEnabled: boolean;
  actions: ReadonlyArray<GlobalHeaderAction>;
}>;

@Component({
  selector: 'app-global-header',
  templateUrl: './global-header.component.html',
  styleUrls: ['./global-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    MatButton,
    MatIconButton,
    MatIcon,
    MatMenuModule,
    MatTooltip,
    TranslateModule,
    ContextSwitcherComponent,
  ],
})
export class GlobalHeaderComponent {
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly intercomService = inject(IntercomService);
  private readonly headerExtras = inject(GlobalHeaderExtrasService);
  private readonly contextService = inject(ContextService);
  private readonly accountService = inject(AccountService);
  private readonly permissionsBackend = inject(PermissionsBackendService);
  private readonly shellNav = inject(ShellNavService);
  private readonly navModel = inject(NavModelService);
  private readonly themeService = inject(ThemeService);

  /** Compact: the actions fold into one overflow menu so the title and switcher keep their room. */
  readonly isCompact = this.shellNav.isCompact;

  readonly isDarkMode = this.themeService.isDarkMode;

  /** `instant()` resolves once, so the label needs a reason to be recomputed when the language changes. */
  private readonly language = toSignal(this.translateService.onLangChange);

  /** Names what the button will do, not what is on, so the label is unambiguous either way. */
  readonly themeToggleLabel = computed(() => {
    this.language();

    const key = this.isDarkMode()
      ? 'SHARED.SYSTEM.SWITCH_TO_LIGHT_MODE'
      : 'SHARED.SYSTEM.SWITCH_TO_DARK_MODE';

    return this.translateService.instant(key) as string;
  });

  toggleTheme(): void {
    this.themeService.toggle();
  }

  readonly visibleActions = computed(() =>
    this.viewModel().actions.filter(action => action.visible !== false)
  );

  /**
   * A menu row needs words. Icon-only actions carry theirs in whichever of the accessible name or
   * the tooltip the caller filled in, so all of them are tried before giving up on the id.
   */
  actionLabel(action: GlobalHeaderAction): string {
    return (
      action.label ??
      this.translate(action.labelTranslateKey) ??
      action.ariaLabel ??
      this.translate(action.ariaLabelTranslateKey) ??
      action.tooltip ??
      this.translate(action.tooltipTranslateKey) ??
      action.id
    );
  }

  /** Null where the label already is the tooltip, so a row never says the same thing twice. */
  disabledReason(action: GlobalHeaderAction): string | null {
    if (action.disabled !== true) {
      return null;
    }

    const reason = action.tooltip ?? this.translate(action.tooltipTranslateKey);

    return reason && reason !== this.actionLabel(action) ? reason : null;
  }

  /** A `disabledInteractive` item still receives the click the browser would have blocked. */
  trigger(action: GlobalHeaderAction): void {
    if (action.disabled !== true) {
      action.onTrigger();
    }
  }

  private translate(key: string | undefined): string | undefined {
    return key ? (this.translateService.instant(key) as string) : undefined;
  }

  private readonly contextSignal = toSignal<ContextObject | null>(
    this.contextService.getContext$().pipe(startWith(null)),
    { initialValue: null }
  );

  private readonly accountSignal = toSignal<{ uuid: string | null } | null>(
    this.accountService.getAccountContext$().pipe(startWith(null)),
    { initialValue: null }
  );

  private readonly permissionsSignal = toSignal<ReadonlyArray<PermissionsEntry> | null>(
    this.permissionsBackend.getPermissions$().pipe(startWith(null)),
    { initialValue: null }
  );

  private canUseAccountContext(accountUuid: string): boolean {
    const permissions = this.permissionsSignal();
    if (!permissions || !accountUuid) {
      return false;
    }

    const entry = permissions.find(p => p.accountUuid === accountUuid);
    return entry?.permissions?.ACCOUNT_USE === true;
  }

  /**
   * The help button opens the Intercom article for the page you are on, so it says which page that
   * is. The title is already resolved and translated; when a page has none, the generic wording
   * still tells you the article is about where you are.
   */
  readonly helpLabel = computed(() => {
    const page = this.viewModel().title.trim();

    return page
      ? (this.translateService.instant('SHARED.SYSTEM.HELP_LEARN_MORE_ABOUT', { page }) as string)
      : (this.translateService.instant('SHARED.SYSTEM.HELP_LEARN_MORE_THIS_PAGE') as string);
  });

  openHelpArticle(intercomArticleId: string): void {
    this.intercomService.openArticleById(intercomArticleId);
  }

  printPage(): void {
    window.print();
  }

  private readonly viewModelSignal = toSignal(
    merge(
      this.router.events.pipe(filter(isNavigationEnd)),
      this.translateService.onLangChange
    ).pipe(
      startWith(null),
      map((): GlobalHeaderViewModel => {
        const { title, breadcrumbs, helpArticleId, printEnabled } = buildGlobalHeaderStaticModel(
          this.router.routerState.snapshot,
          this.translateService
        );

        return {
          title,
          titleCount: null,
          breadcrumbs,
          helpArticleId,
          printEnabled,
          actions: [],
        };
      })
    ),
    {
      initialValue: {
        title: '',
        titleCount: null,
        breadcrumbs: [],
        helpArticleId: null,
        printEnabled: false,
        actions: [],
      } satisfies GlobalHeaderViewModel,
    }
  );

  private withVenueContextBreadcrumbPrefix(
    breadcrumbs: ReadonlyArray<BreadcrumbItem>
  ): ReadonlyArray<BreadcrumbItem> {
    const context = this.contextSignal();
    if (!context || !ContextService.isVenueContext(context)) {
      return breadcrumbs;
    }

    const accountName = (context.accountName ?? '').trim();
    const venueName = (context.venueName ?? '').trim();
    const venueUuid = (context.uuid ?? '').trim();
    const accountUuid = (this.accountSignal()?.uuid ?? '').trim();

    if (!accountName || !venueName || !venueUuid || !accountUuid) {
      return breadcrumbs;
    }

    const accountUrl = this.canUseAccountContext(accountUuid)
      ? `/${CONSTANTS.ROUTES.ACCOUNT}/${accountUuid}/${CONSTANTS.ROUTES.DASHBOARD}`
      : null;

    const first = breadcrumbs.at(0);
    const firstUrl = first?.url;
    const firstIsVenue =
      typeof firstUrl === 'string' && firstUrl.startsWith(`/${CONSTANTS.ROUTES.VENUE}/`);
    const venueUrl = firstIsVenue ? firstUrl : `/${CONSTANTS.ROUTES.VENUE}/${venueUuid}`;

    const rest = firstIsVenue ? breadcrumbs.slice(1) : breadcrumbs;

    return [{ label: accountName, url: accountUrl }, { label: venueName, url: venueUrl }, ...rest];
  }

  /**
   * Names the nav section the current page belongs to, linking to its landing page. It comes
   * from the nav model, not the router: a page's section is not reliably its URL prefix —
   * Campaigns sits under both Analytics and Surveys, the Surveys copy routed under `settings/`.
   */
  private withSectionBreadcrumb(
    breadcrumbs: ReadonlyArray<BreadcrumbItem>
  ): ReadonlyArray<BreadcrumbItem> {
    const activeKey = this.navModel.activeGroupKey();
    const group = activeKey
      ? this.navModel.groups().find(candidate => candidate.key === activeKey)
      : undefined;

    // A group with no sub-destinations is the page itself, and would name itself twice.
    if (!group?.link || group.sections.length === 0) {
      return breadcrumbs;
    }

    // Already present — this is the landing page, or the trail was built with it.
    if (breadcrumbs.some(crumb => crumb.url === group.link)) {
      return breadcrumbs;
    }

    const section: BreadcrumbItem = {
      label: String(this.translateService.instant(group.title)),
      url: group.link,
    };

    const firstUrl = breadcrumbs.at(0)?.url;
    const insertAt = typeof firstUrl === 'string' && CONTEXT_ROOT_URL.test(firstUrl) ? 1 : 0;

    return [...breadcrumbs.slice(0, insertAt), section, ...breadcrumbs.slice(insertAt)];
  }

  private decorateBreadcrumbs(
    breadcrumbs: ReadonlyArray<BreadcrumbItem>
  ): ReadonlyArray<BreadcrumbItem> {
    return this.withVenueContextBreadcrumbPrefix(this.withSectionBreadcrumb(breadcrumbs));
  }

  readonly viewModel = computed(() => ({
    ...this.viewModelSignal(),
    title: this.headerExtras.titleOverride() ?? this.viewModelSignal().title,
    titleCount: this.headerExtras.titleCount(),
    breadcrumbs: (() => {
      const base = this.viewModelSignal().breadcrumbs;
      if (base.length === 0) {
        return this.withVenueContextBreadcrumbPrefix(base);
      }

      const fromEnd = this.headerExtras.breadcrumbLabelOverridesFromEnd();
      const keys = Object.keys(fromEnd);
      const tailOverride = this.headerExtras.lastBreadcrumbLabelOverride();

      if (keys.length === 0 && !tailOverride) {
        return this.decorateBreadcrumbs(base);
      }

      const next = base.slice();

      if (keys.length > 0) {
        for (const key of keys) {
          const indexFromEnd = Number(key);
          if (!Number.isFinite(indexFromEnd) || indexFromEnd < 1 || indexFromEnd > next.length) {
            continue;
          }
          const label = fromEnd[key];
          if (typeof label !== 'string' || label.trim().length === 0) {
            continue;
          }
          const targetIndex = next.length - indexFromEnd;
          next[targetIndex] = {
            ...next[targetIndex],
            label,
          };
        }
      }

      // Convenience/legacy override: always replaces the final breadcrumb label.
      if (tailOverride) {
        next[next.length - 1] = {
          ...next[next.length - 1],
          label: tailOverride,
        };
      }

      return this.decorateBreadcrumbs(next);
    })(),
    actions: this.headerExtras.actions(),
  }));
}
